import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Crown, MessageSquareText } from 'lucide-react';

import { getReviewLists } from '../../../services/StudentFeedbackService';
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import ReviewListPopup from './ReviewListPopup';
import useScrollLoadMore from '../common/useScrollLoadMore';
import '../common/waypoint/waypoint.css';
import '../common/lms-pages.css';
import '../Trainer/TrainerDashboard.css';
import './feedback-list.css';

const PAGE_SIZE = 10;

// How many trainers at the top of the good list are "student favourites".
export const FAVOURITE_COUNT = 5;

const TABS = [
  { key: 'trainer', label: 'Trainers', countKey: 'trainers' },
  { key: 'batch', label: 'Batches', countKey: 'batches' },
];

/* What each kind is called. The ratings each one covers come from the API
   (`range`), so the wording can never disagree with what was counted. */
const KIND_COPY = {
  bad: {
    title: 'Bad feedbacks',
    countedAs: (r) => `Rated ${r.max} and below`,
    avgLabel: 'Avg bad rating',
    avgNote: 'The average of those bad ratings',
    countLabel: 'Bad feedbacks',
    pill: 'pgm-pill--bad',
    loading: 'Loading bad feedbacks...',
    empty: (r) => `No bad feedbacks — nothing rated ${r.max} or below for these filters.`,
  },
  good: {
    title: 'Good feedbacks',
    countedAs: (r) => `Rated ${r.min} and above`,
    avgLabel: 'Avg good rating',
    avgNote: 'The average of those good ratings',
    countLabel: 'Good feedbacks',
    pill: 'pgm-pill--good',
    loading: 'Loading good feedbacks...',
    empty: (r) => `No good feedbacks — nothing rated ${r.min} or above for these filters.`,
  },
};

/**
 * The bad -- or good -- reviews behind a section, by trainer and by batch.
 *
 * The house view popup (List Attendance's): pgm-head, a table body and
 * Close alone in the footer. Both tabs are paged by the SERVER
 * (/studentfeedbacks/review-lists) with the page's own filters, and every
 * figure in them -- the rank included -- is worked out there.
 *
 * In the good list, the first FAVOURITE_COUNT trainers are the students'
 * favourites: a gold row and a crown. Every trainer and batch name opens
 * the bridge (waypoint) popup for it.
 */
const ReviewsPopup = ({ kind = 'bad', params, onClose }) => {
  const copy = KIND_COPY[kind] || KIND_COPY.bad;
  const { open: openEntity } = useWaypoint();
  const [tab, setTab] = useState('trainer');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const listEndRef = useRef(null);
  const [counts, setCounts] = useState({ trainers: 0, batches: 0 });
  const [range, setRange] = useState(kind === 'good' ? { min: 4, max: 5 } : { min: 1, max: 3 });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // The row whose reviews are open on top: { title, sub, filter }, or null.
  const [reviewing, setReviewing] = useState(null);

  // Which request the answer belongs to: a slow page must not land on top
  // of a tab or page chosen after it.
  const seq = useRef(0);

  // The params object is rebuilt on every parent render; its contents are
  // what the fetch depends on.
  const paramsKey = JSON.stringify(params || {});

  /* A page at a time from the server, appended as the list is scrolled
     (useScrollLoadMore below); switching tab starts it again from page 1. */
  useEffect(() => {
    const mine = ++seq.current;
    const append = page > 1;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setFailed(false);
    getReviewLists({ ...JSON.parse(paramsKey), kind, groupBy: tab, page, limit: PAGE_SIZE })
      .then((res) => {
        if (mine !== seq.current) return;
        setTotal(Number(res.pagination?.total) || 0);
        setCounts(res.counts);
        setRange(res.range);
        setRows((prev) => {
          if (!append) return res.data;
          const keyOf = (r) => String(r.trainerId || r.batchId || r.rank);
          const seen = new Set(prev.map(keyOf));
          return [...prev, ...res.data.filter((r) => !seen.has(keyOf(r)))];
        });
      })
      .catch(() => {
        if (mine !== seq.current) return;
        if (!append) {
          setRows([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (mine !== seq.current) return;
        setLoading(false);
        setLoadingMore(false);
      });
  }, [kind, tab, page, paramsKey]);

  const hasMore = rows.length < total;
  // The sentinel is in the popup's scrolling body; the hook is rooted at the
  // viewport, which that body's scroll clips, so it works in here unchanged.
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || failed || !hasMore) return;
      setPage((p) => p + 1);
    },
    [rows.length, total, loading, loadingMore, failed, tab],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // A trainer, batch or review list opened from here sits on top;
      // Escape is its first.
      if (document.querySelector('.wp-modal, .rvl-modal')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const switchTab = (key) => {
    if (key === tab) return;
    setTab(key);
    setPage(1);
    setRows([]);
  };

  const isFavourite = (row) =>
    kind === 'good' && tab === 'trainer' && Number(row.rank) > 0 && Number(row.rank) <= FAVOURITE_COUNT;

  const trainerLink = (id, name) =>
    id ? (
      <button type="button" className="wp-link" onClick={() => openEntity('trainer', id)}>
        {name || 'Unknown trainer'}
      </button>
    ) : (
      name || 'Unknown trainer'
    );

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rvp-title"
      onClick={onClose}
    >
      {/* --xl: the house's large popup (1060px, fixed height, body scrolls).
          Up to seven columns with full batch names do not fit in --wide. */}
      <div className="pg-modal-panel pg-modal-panel--xl" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="rvp-title">{copy.title}</h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Counted as {kind}</dt>
                <dd>{copy.countedAs(range)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>{copy.avgLabel}</dt>
                <dd>{copy.avgNote}, with the page’s filters</dd>
              </div>
              {kind === 'good' && (
                <div className="pgm-ident-row">
                  <dt>Student favourites</dt>
                  <dd>The top {FAVOURITE_COUNT} trainers, by how many students rated them well</dd>
                </div>
              )}
            </dl>
          </div>

          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="lp-tabs" role="tablist" aria-label={`${copy.title} by`}>
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              role="tab"
              id={`rvp-tab-${t.key}`}
              aria-selected={tab === t.key}
              aria-controls="rvp-panel"
              className={`lp-tab${tab === t.key ? ' is-on' : ''}`}
              onClick={() => switchTab(t.key)}
            >
              {t.label}
              <span className="lp-tab-n">{counts[t.countKey] || 0}</span>
            </button>
          ))}
        </div>

        <div className="pg-modal-body" id="rvp-panel" role="tabpanel" aria-labelledby={`rvp-tab-${tab}`}>
          {loading && rows.length === 0 ? (
            <div className="att-picklist-foot">
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>{copy.loading}</span>
            </div>
          ) : failed ? (
            <p className="pgm-empty">That did not load. Try again in a moment.</p>
          ) : rows.length === 0 ? (
            <p className="pgm-empty">{copy.empty(range)}</p>
          ) : (
            <div className="pgm-tablewrap brp-tablewrap">
              <table className="pgm-table brp-table">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    {tab === 'batch' ? (
                      <>
                        <th>Batch</th>
                        <th>Trainer</th>
                      </>
                    ) : (
                      <th>Trainer</th>
                    )}
                    <th className="pgm-num">{copy.avgLabel}</th>
                    <th className="pgm-num">{copy.countLabel}</th>
                    <th className="pgm-num">Students</th>
                    {tab === 'trainer' && <th className="pgm-num">Batches</th>}
                    <th aria-label="Feedbacks" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const favourite = isFavourite(row);
                    return (
                      <tr key={row.trainerId || row.batchId || i} className={favourite ? 'rvp-fav' : undefined}>
                        <td className="pgm-num">{row.rank || i + 1}</td>
                        {tab === 'batch' ? (
                          <>
                            <td>
                              <button
                                type="button"
                                className="wp-link"
                                onClick={() => openEntity('batch', row.batchId)}
                              >
                                {row.batchName || 'Unnamed batch'}
                              </button>
                            </td>
                            <td>
                              {(row.trainers || []).length === 0
                                ? '—'
                                : row.trainers.map((t, j) => (
                                  <span key={t.id}>
                                    {j > 0 && ', '}
                                    {trainerLink(t.id, t.name)}
                                  </span>
                                ))}
                            </td>
                          </>
                        ) : (
                          <td>
                            <span className="rvp-trainer">
                              {favourite && <Crown className="rvp-crown" aria-hidden="true" />}
                              {trainerLink(row.trainerId, row.trainerName)}
                              {favourite && <span className="rvp-fav-tag">Student favourite</span>}
                            </span>
                          </td>
                        )}
                        <td className="pgm-num">
                          {Number(row.averageReviewRating) > 0 ? (
                            <span className={`pgm-pill ${copy.pill}`}>
                              {Number(row.averageReviewRating).toFixed(1)} / 5
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="pgm-num">{row.reviewCount}</td>
                        <td className="pgm-num">{row.studentCount}</td>
                        {tab === 'trainer' && <td className="pgm-num">{row.batchCount}</td>}
                        <td className="pgm-num">
                          <button
                            type="button"
                            className="pgm-btn brs-reviews-btn"
                            aria-haspopup="dialog"
                            onClick={() =>
                              setReviewing(
                                tab === 'batch'
                                  ? { title: row.batchName || 'Batch', sub: 'Every feedback for this batch', filter: { batchId: row.batchId } }
                                  : { title: row.trainerName || 'Trainer', sub: 'Every feedback for this trainer', filter: { trainerId: row.trainerId } },
                              )
                            }
                            disabled={tab === 'trainer' && !row.trainerId}
                          >
                            <MessageSquareText aria-hidden="true" />
                            Feedbacks
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Where the reader is, and the sentinel the observer watches. */}
          <div className="pg-scroll-foot" ref={listEndRef}>
            {hasMore ? (
              <>
                <span className="pg-scroll-count">
                  {rows.length} of {total}
                </span>
                {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
              </>
            ) : total > 0 ? (
              <span className="pg-scroll-count">
                All {total} {tab === 'batch' ? (total === 1 ? 'batch' : 'batches') : (total === 1 ? 'trainer' : 'trainers')} loaded
              </span>
            ) : null}
          </div>
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>

        {/* Inside the panel, whose onClick stops propagation: a click in the
            review list must not reach this popup's backdrop and close it. */}
        {reviewing && createPortal(
          <ReviewListPopup
            title={reviewing.title}
            sub={reviewing.sub}
            filter={reviewing.filter}
            baseParams={params}
            initialBand={kind}
            onClose={() => setReviewing(null)}
          />,
          document.body,
        )}
      </div>
    </div>
  );
};

export default ReviewsPopup;
